// src/pages/MenteeDashboard.js
import ChatPopup from '../components/ChatPopup';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../axiosInstance';
import MenteeCalendar from '../components/MenteeCalendar';
import MentoringJournal from '../components/MentoringJournal';
import MentoringAgreementForm from '../components/MentoringAgreement/MentoringAgreementForm';
import JitsiMeet from '../components/JitsiMeet';
import '../components/MatchingResultPage.css';
import FeedbackTabs from '../components/FeedbackTabs';
import MentoringAgreementTile from '../components/MentoringAgreementTile';
import { MessageCircle } from 'lucide-react';
import MentorMatchInfo from '../components/MentorMatchInfo';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { useTranslation, Trans } from 'react-i18next';

// ► NEU: Mentoring-Session sicherstellen (SSO-Attach, falls nötig)
import ensureMentoringSession from '../utils/ensureMentoringSession';

// ► Pairing-Context für Multi-Pairing-Auswahl
import { usePairing } from '../context/PairingContext';

// ► Basis-URL für API-Calls (gleiche Origin als Default)
const API = process.env.REACT_APP_API_URL || '';

/**
 * ► NEU: Mentee informiert beim ersten Chat-Kontakt den Mentor via Backend-Mail
 * Wird vor dem Öffnen des Chat-Popups aufgerufen.
 */
async function notifyFirstContact(mentorUserId) {
  try {
    await fetch(`${API}/mentoring/notify/first-contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ mentor_user_id: mentorUserId }),
    });
    console.log('first-contact notify sent for', mentorUserId);
  } catch (e) {
    console.warn('first-contact notify failed', e);
  }
}

function MenteeDashboard() {
  const { t, i18n } = useTranslation();
  const { selectedMatchingId } = usePairing();

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [allMentors, setAllMentors] = useState([]);
  const [mentor, setMentor] = useState(null);

  const [agreementOpen, setAgreementOpen] = useState(false);

  // Pair-Chat (Mentee ↔ Mentor)
  const [chatOpen, setChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // HR-Chat (Mentee ↔ HR) — strikt getrennt
  const [hrChatOpen, setHrChatOpen] = useState(false);
  const [hrUnreadCount, setHrUnreadCount] = useState(0);

  const [videoOpen, setVideoOpen] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const [agreement, setAgreement] = useState(null);
  const [activeTab, setActiveTab] = useState('vereinbarung');
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false); // Modal-Status (offen oder geschlossen)
  const [deleteStep, setDeleteStep] = useState('info');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // ✅ NEU: Bestätigungswort abhängig von Sprache
  const isGerman = String(i18n.language || 'de').toLowerCase().startsWith('de');
  const deleteKeyword = isGerman ? 'LÖSCHEN' : 'DELETE';

  // ----- Profil laden -----
  useEffect(() => {
    (async () => {
      // ► NEU: Vor ersten Mentoring-API-Calls Session beim Mentoring-Dienst sicherstellen
      try {
        await ensureMentoringSession();
      } catch (_) {
        // still tolerieren – der folgende Call entscheidet
      }

      axios
        .get(`/mentoring/mentee/profile`)
        .then((res) => setUser(res.data))
        .catch(() => setUser(null));
    })();
  }, []);

  // ----- Feedback-Status (Badge im Tab) -----
  useEffect(() => {
    if (!user?.user_id) return;
    axios
      .get(`/mentoring/feedback/status/${user.user_id}${selectedMatchingId ? `?matching_id=${selectedMatchingId}` : ''}`)
      .then(res => setFeedbackOpen(!!res.data.open))
      .catch(() => setFeedbackOpen(false));
  }, [user, selectedMatchingId]);

  // ----- Alle Matched-Mentoren laden (Plural) -----
  useEffect(() => {
    if (!user?.user_id) {
      setAllMentors([]);
      return;
    }
    axios
      .get(`/mentoring/mentee/${user.user_id}/matched-mentors`)
      .then((res) => {
        setAllMentors(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('matched-mentors fetch failed:', err);
        setAllMentors([]);
      });
  }, [user, t]);

  // ----- Aktiven Mentor per selectedMatchingId picken -----
  useEffect(() => {
    if (allMentors.length === 0) {
      setMentor(null);
      return;
    }
    const picked = selectedMatchingId
      ? allMentors.find((m) => m.matching_id === selectedMatchingId)
      : null;
    const chosen = picked || allMentors[0];
    if (!chosen) {
      setMentor(null);
      return;
    }
    setMentor({
      name: `${chosen.first_name} ${chosen.last_name}`,
      email: chosen.email,
      title: chosen.job_title || t('menteeDashboard.noPosition'),
      company: chosen.abteilung || t('menteeDashboard.noDepartment'),

      // ✅ NEU: Beschreibung aus API übernehmen (beide Keys unterstützen)
      profileDescription: chosen.profileDescription ?? chosen.profile_description ?? '',

      avatar: chosen.avatar_path
        ? `${API}/uploads/${chosen.avatar_path}`
        : '/default-avatar.png',
      experience: chosen.experience || '',
      sharedGoals: chosen.goals || [],
      sharedCompetences: chosen.competences || [],
      surveyId:          chosen.survey_id || null,
      applicationFields: chosen.application_fields || [],
      methodsSkills:     chosen.methods_skills || [],
      score: chosen.score || null,
      fachlicherScore: chosen.fachlicher_score || null,
      persoenlicherScore: chosen.persoenlicher_score || null,
      confirmed: chosen.confirmed_by_mentee || false,
      id: chosen.mentor_id,
      matching_id: chosen.matching_id
    });
  }, [allMentors, selectedMatchingId, t]);

  // ----- Mentoring-Vereinbarung holen -----
  useEffect(() => {
    if (!user?.user_id) return;
    axios
      .get(`/mentoring/agreement/mentee/${user.user_id}`)
      .then((res) => setAgreement(res.data))
      .catch(() => setAgreement(null));
  }, [user]);

  // ----- Unread (Mentor→Mentee) für Badge am Mentor-Chat -----
  useEffect(() => {
    const menteeId = user?.user_id;
    const mentorId = mentor?.id;

    if (!menteeId || !mentorId) {
      setUnreadCount(0);
      return;
    }

    let cancelled = false;

    const fetchMentorUnreadCount = async () => {
      try {
        const url =
          `${API}/mentoring/chat/unread/mentor-to-mentee` +
          `?menteeId=${encodeURIComponent(menteeId)}` +
          `&mentorId=${encodeURIComponent(mentorId)}` +
          `&_=${Date.now()}`;

        const res = await fetch(url, {
          credentials: 'include',
          cache: 'no-store',
        });

        if (!res.ok) {
          console.error('Fehler beim Laden mentor-unread:', res.status, await res.text());
          if (!cancelled) setUnreadCount(0);
          return;
        }

        const data = await res.json();
        if (!cancelled) {
          setUnreadCount(data.unread ?? data.unreadCount ?? 0);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Fehler beim Laden mentor-unread:', err);
          setUnreadCount(0);
        }
      }
    };

    // sofort einmal laden
    fetchMentorUnreadCount();
    // dann alle 30 Sekunden neu holen
    const intervalId = setInterval(fetchMentorUnreadCount, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user, mentor]);

  // ----- Unread für HR-Chat Badge -----
  useEffect(() => {
    if (!user?.user_id) return;
    axios
      .get(`/mentoring/chat/unread/mentee`)
      .then((res) => setHrUnreadCount(res.data.count))
      .catch(() => {});
  }, [user]);

  // ----- Mentor-Chat öffnen -----
  const handleOpenChat = async (mentorObj) => {
    if (!user || !mentorObj) return;

    // ► NEU: Mentor über den ersten Kontakt per Mail benachrichtigen
    await notifyFirstContact(mentorObj.id);

    setChatContext({
      menteeId: user.user_id,
      mentorId: mentorObj.id,
      senderRole: 'mentee',
      receiverRole: 'mentor',
      presetMessage: ''
    });
    setChatOpen(true);
    setUnreadCount(0); // Badge lokal zurücksetzen; Server-Markierung macht ChatPopup
  };

  // ----- HR-Chat öffnen (funktioniert auch ohne Mentor-Zuordnung) -----
  const handleOpenHrChat = () => {
    if (!user?.user_id) return;
    setHrChatOpen(true);
    setHrUnreadCount(0); // Badge lokal zurücksetzen; Server-Markierung macht ChatPopup
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteStep('info');
    setDeleteConfirmText('');
  };

  const handleDeactivateAccount = async () => {
    if (!user?.user_id) return;

    try {
      await axios.post(
        `/mentoring/account/delete-request`,
        {}
      );

      // Schritt "done" kommt gleich im nächsten Schritt – erstmal nur schließen
      closeDeleteModal();
      alert(t('menteeDashboard.deleteAccountDeactivatedInfo'));
    } catch (err) {
      console.error('Deaktivierung fehlgeschlagen:', err);
      alert(t('menteeDashboard.somethingWentWrong'));
    }
  };

  // ----- Externes openChatPopup-Event (optional) -----
  useEffect(() => {
    const handleChatOpen = (e) => {
      const { menteeId, mentorId, senderRole, receiverRole, presetMessage } =
        (e.detail || {});
      if (user?.user_id !== menteeId) return;
      setChatOpen(true);
      setChatContext({
        menteeId,
        mentorId,
        senderRole,
        receiverRole,
        presetMessage: presetMessage || ''
      });
    };
    window.addEventListener('openChatPopup', handleChatOpen);
    return () => window.removeEventListener('openChatPopup', handleChatOpen);
  }, [user]);

  // ----- Match bestätigen / ablehnen -----
  const handleConfirmMatch = async () => {
    if (!user || !mentor) return;
    try {
      await axios.patch(
        `/mentoring/matching/confirm-by-mentee`,
        { menteeId: user.user_id, mentorId: mentor.id }
      );
      setMentor((prev) => ({ ...prev, confirmed: true }));
      alert(t('menteeDashboard.matchConfirmed'));
    } catch {
      alert(t('menteeDashboard.somethingWentWrong'));
    }
  };

  const handleRejectMatch = async () => {
    if (!user || !mentor) return;
    try {
      await axios.delete(`/mentoring/matching/remove-match`, {
        data: { menteeId: user.user_id, mentorId: mentor.id }
      });
      setMentor(null);
      setShowRejectDialog(false);
      alert(t('menteeDashboard.matchRemoved'));
    } catch {
      alert(t('menteeDashboard.somethingWentWrong'));
    }
  };

  const userId = user?.user_id;
  const jitsiLang = (i18n.language || 'de').split('-')[0];

  // ✅ NEU: Gemeinsamen Raumname aus beiden IDs bauen (sortiert => immer identisch auf beiden Seiten)
  const buildRoomName = (menteeId, mentorId) => {
    const a = Number(menteeId);
    const b = Number(mentorId);
    if (!a || !b) return null;
    const [minId, maxId] = a < b ? [a, b] : [b, a];
    return `mentoring-${minId}-${maxId}`;
  };

  return (
    <div className="min-h-screen bg-black text-white p-6 font-sans">
      {/* Top-Leiste */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex gap-4 items-center flex-grow">
          <Link
            to="/mentee-profile"
            className="bg-mentixxgreen hover:bg-green-500 focus:ring-2 focus:ring-mentixxgreen text-black font-bold py-2 px-5 rounded-2xl shadow transition"
          >
            {t('menteeDashboard.profile')}
          </Link>

          <a
            href="https://greentechtalents.oncademy.de/users/sign_in"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-mentixxgreen hover:bg-green-500 focus:ring-2 focus:ring-mentixxgreen text-black font-bold py-2 px-5 rounded-2xl shadow transition"
          >
            {t('menteeDashboard.elearning')}
          </a>

          {/* HR-Chat Button mit Badge */}
          <button
            title={t('menteeDashboard.hrChatOpen')}
            onClick={handleOpenHrChat}
            className="relative bg-transparent ml-1 p-1 rounded-full hover:text-mentixxgreen focus:outline-none focus:ring-2 focus:ring-mentixxgreen transition"
            style={{ lineHeight: 0 }}
          >
            <MessageCircle size={22} />
            {hrUnreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {hrUnreadCount}
              </span>
            )}
          </button>

          {/* „Konto löschen“-Button rechts ausrichten */}
          <Button
            onClick={() => setDeleteModalOpen(true)}
            className="bg-red-500 text-white py-2 px-4 rounded-full mt-4 ml-auto"
          >
            {t('menteeDashboard.deleteAccount')}
          </Button>
        </nav>
      </div>

      {/* Konto löschen – Modal */}
      <Dialog
        open={deleteModalOpen}
        onOpenChange={(open) => {
          if (!open) closeDeleteModal();
          else setDeleteModalOpen(true);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('menteeDashboard.deleteAccount')}</DialogTitle>
          </DialogHeader>

          {deleteStep === 'info' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                {t('menteeDashboard.deleteInfoLine1')}
              </p>
              <p className="text-sm text-gray-700">
                {t('menteeDashboard.deleteInfoLine2')}
              </p>
              <p className="text-sm text-gray-700">
                {t('menteeDashboard.deleteInfoLine3')}
              </p>

              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteModal}>
                  {t('menteeDashboard.cancel')}
                </Button>
                <Button onClick={() => setDeleteStep('confirm')}>
                  {t('menteeDashboard.continue')}
                </Button>
              </div>
            </div>
          )}

          {deleteStep === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                <Trans
                  i18nKey="menteeDashboard.deleteConfirmHint"
                  values={{ keyword: deleteKeyword }}
                  components={{ b: <strong /> }}
                />
              </p>

              <input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={deleteKeyword}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-black"
              />

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDeleteStep('info')}>
                  {t('menteeDashboard.back')}
                </Button>

                <Button
                  className="bg-red-600 text-white"
                  disabled={deleteConfirmText.trim().toUpperCase() !== deleteKeyword}
                  onClick={handleDeactivateAccount}
                >
                  {t('menteeDashboard.deactivateNow')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Mentor-Info / Aktionen */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h2 className="font-bold mb-2">{t('menteeDashboard.mentorProfile')}</h2>
          {mentor ? (
            <>
              <MentorMatchInfo
                mentor={mentor}
                matchConfirmed={mentor.confirmed}
                onConfirm={handleConfirmMatch}
                onReject={() => setShowRejectDialog(true)}
                onShowDetails={() => setDetailsOpen(true)}
                onSendMessage={handleOpenChat}
                onStartVideoCall={() => setVideoOpen(true)}
                unreadCount={unreadCount}
              />
            </>
          ) : (
            <div className="text-gray-300">{t('menteeDashboard.noMentor')}</div>
          )}
        </div>

        {/* Mentor-Chat */}
        {chatOpen && chatContext && (
          <ChatPopup
            menteeId={chatContext.menteeId}
            mentorId={chatContext.mentorId}
            senderRole={chatContext.senderRole}
            receiverRole={chatContext.receiverRole}
            presetMessage={chatContext.presetMessage}
            onClose={() => {
              setChatOpen(false);
              setChatContext(null);
            }}
          />
        )}

        {/* HR-Chat – strikt getrennt; funktioniert auch ohne Mentor */}
        {hrChatOpen && user && (
          <ChatPopup
            menteeId={user.user_id}
            mentorId={mentor?.id ?? null}
            senderRole="mentee"
            receiverRole="hr"
            threadType="hr"
            presetMessage=""
            onClose={() => setHrChatOpen(false)}
          />
        )}

        {/* Video-Call */}
        {videoOpen && user && mentor?.id && (
          <JitsiMeet
            roomName={buildRoomName(user.user_id, mentor.id)}
            displayName={`${(user.first_name ?? user.firstName ?? '')} ${(user.last_name ?? user.lastName ?? '')}`.trim()}
            language={jitsiLang}
            matchingId={selectedMatchingId}
            peerUserId={mentor.id}
            peerRole="mentor"
            onClose={() => setVideoOpen(false)}
          />
        )}

        {/* Kalender */}
        <div className="bg-gray-800 p-4 rounded-2xl shadow">
          <h2 className="font-bold mb-2 text-white">{t('menteeDashboard.calendar')}</h2>
          <MenteeCalendar user={user} mentor={mentor} />
        </div>

        {/* Journal */}
        {userId && (
          <div className="bg-gray-800 p-4 rounded-2xl shadow">
            <h2 className="font-bold mb-2">{t('menteeDashboard.journal')}</h2>
            <MentoringJournal user={user} viewerRole="mentee" mentor={mentor} />
          </div>
        )}

        {/* Vereinbarung & Feedback Tabs */}
        {user && (
          <div className="bg-gray-800 p-4 rounded-2xl shadow">
            <h2 className="font-bold mb-2">{t('menteeDashboard.agreementAndFeedback')}</h2>
            <div className="flex gap-3 mb-5">
              <button
                className={`px-4 py-2 rounded-xl font-semibold transition ${
                  activeTab === 'vereinbarung'
                    ? 'bg-mentixxgreen text-black'
                    : 'bg-[#181f29] text-white'
                }`}
                onClick={() => setActiveTab('vereinbarung')}
              >
                {t('menteeDashboard.agreement')}
              </button>
              <button
                className={`px-4 py-2 rounded-xl font-semibold transition relative ${
                  activeTab === 'feedback'
                    ? 'bg-mentixxgreen text-black'
                    : 'bg-[#181f29] text-white'
                }`}
                onClick={() => setActiveTab('feedback')}
              >
                {t('menteeDashboard.feedback')}
                {feedbackOpen && (
                  <span className="absolute top-0 -right-4 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    !
                  </span>
                )}
              </button>
            </div>

            {/* Vereinbarung */}
            {activeTab === 'vereinbarung' && (
              <>
                <Button
                  onClick={() => {
                    console.log('[UI] open agreement form click');
                    setAgreementOpen(true);
                  }}
                  className="border border-mentixxgreen text-mentixxgreen w-full py-1 rounded mb-5"
                >
                  {t('menteeDashboard.editAgreement') || 'Mentoringvereinbarung bearbeiten'}
                </Button>

                <MentoringAgreementTile user={user} matchingId={selectedMatchingId} />
                {agreementOpen && user && (
                  <Dialog open={agreementOpen} onOpenChange={setAgreementOpen}>
                    <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{t('menteeDashboard.agreement')}</DialogTitle>
                      </DialogHeader>
                      <MentoringAgreementForm
                        user={user}
                        mentor={mentor}
                        menteeName={
                          user && user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user?.first_name || user?.last_name || '-'
                        }
                        mentorName={mentor?.name || t('menteeDashboard.exampleMentor')}
                        goals={mentor?.sharedGoals || []}
                        competences={mentor?.sharedCompetences || []}
                        whyMentoring={user?.why_mentoring || ''}
                        onClose={() => setAgreementOpen(false)}
                      />
                    </DialogContent>
                  </Dialog>
                )}
              </>
            )}

            {/* Feedback */}
            {activeTab === 'feedback' && (
              <FeedbackTabs userId={user.user_id} user={user} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MenteeDashboard;
